/**
 * AuditItemModal — Edit audit item compliance status, risk, evidence, and remediation
 *
 * Schema fields (from schema-compliance.ts / compliance-audits.ts):
 *  - complianceStatus: compliant | non_compliant | partially_compliant | not_applicable
 *  - riskLevel: critical | high | medium | low | informational
 *  - findingDescription: text (NOT evidenceNotes)
 *  - evidence: text (NOT evidenceLink — plain text)
 *  - remediationAction: text (NOT remediationNotes)
 *  - remediationRequired: boolean
 *  - remediationDueDate: date
 *  - remediationCompletedDate: date
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditItemData {
  id: string;
  itemCode: string | null;
  itemCategory: string;
  itemDescription: string;
  complianceStatus: string;
  riskLevel: string | null;
  findingDescription: string | null;
  evidence: string | null;
  remediationRequired: boolean | null;
  remediationAction: string | null;
  remediationDueDate: string | null;
  remediationCompletedDate: string | null;
}

interface AuditItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: AuditItemData | null;
  auditId: string;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLIANCE_STATUSES = [
  { value: "compliant", label: "Compliant" },
  { value: "non_compliant", label: "Non-Compliant" },
  { value: "partially_compliant", label: "Partially Compliant" },
  { value: "not_applicable", label: "Not Applicable" },
];

const RISK_LEVELS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "informational", label: "Informational" },
];

const SHOW_RISK_STATUSES = ["non_compliant", "partially_compliant"];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuditItemModal({
  open,
  onOpenChange,
  item,
  auditId,
  onSuccess,
}: AuditItemModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [complianceStatus, setComplianceStatus] = useState("not_applicable");
  const [riskLevel, setRiskLevel] = useState("none");
  const [findingDescription, setFindingDescription] = useState("");
  const [evidence, setEvidence] = useState("");
  const [remediationRequired, setRemediationRequired] = useState(false);
  const [remediationAction, setRemediationAction] = useState("");
  const [remediationDueDate, setRemediationDueDate] = useState("");
  const [remediationCompletedDate, setRemediationCompletedDate] = useState("");

  useEffect(() => {
    if (open && item) {
      setComplianceStatus(item.complianceStatus ?? "not_applicable");
      setRiskLevel(item.riskLevel ?? "none");
      setFindingDescription(item.findingDescription ?? "");
      setEvidence(item.evidence ?? "");
      setRemediationRequired(item.remediationRequired ?? false);
      setRemediationAction(item.remediationAction ?? "");
      setRemediationDueDate(item.remediationDueDate ?? "");
      setRemediationCompletedDate(item.remediationCompletedDate ?? "");
    } else if (open && !item) {
      setComplianceStatus("not_applicable");
      setRiskLevel("none");
      setFindingDescription("");
      setEvidence("");
      setRemediationRequired(false);
      setRemediationAction("");
      setRemediationDueDate("");
      setRemediationCompletedDate("");
    }
  }, [open, item]);

  const showRiskLevel = SHOW_RISK_STATUSES.includes(complianceStatus);

  const patchMutation = useMutation({
    mutationFn: async () => {
      if (!item?.id) throw new Error("No item ID");
      const payload: Record<string, unknown> = {
        complianceStatus,
        findingDescription: findingDescription.trim() || undefined,
        evidence: evidence.trim() || undefined,
        remediationRequired,
        remediationAction: remediationAction.trim() || undefined,
        remediationDueDate: remediationDueDate || undefined,
        remediationCompletedDate: remediationCompletedDate || undefined,
      };
      if (showRiskLevel && riskLevel !== "none") {
        payload.riskLevel = riskLevel;
      }
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/security-audit-items/${item.id}`,
        payload
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to update item");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/security-audits/${auditId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-dashboard"] });
      toast({ title: "Audit item updated" });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (!item) return null;

  const title = item.itemCode
    ? `${item.itemCode} — ${item.itemDescription}`
    : item.itemDescription;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="modal-audit-item-form"
      >
        <DialogHeader>
          <DialogTitle className="text-base leading-snug">{title}</DialogTitle>
          <DialogDescription>{item.itemCategory}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Compliance Status */}
          <div className="space-y-1.5">
            <Label>
              Compliance Status <span className="text-destructive">*</span>
            </Label>
            <Select value={complianceStatus} onValueChange={setComplianceStatus}>
              <SelectTrigger data-testid="select-compliance-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {COMPLIANCE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Risk Level — shown only when non-compliant / partial */}
          {showRiskLevel && (
            <div className="space-y-1.5">
              <Label>Risk Level</Label>
              <Select value={riskLevel} onValueChange={setRiskLevel}>
                <SelectTrigger data-testid="select-risk-level">
                  <SelectValue placeholder="Select risk level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {RISK_LEVELS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Finding Description */}
          <div className="space-y-1.5">
            <Label htmlFor="finding-description">Finding Description</Label>
            <Textarea
              id="finding-description"
              data-testid="input-finding-description"
              value={findingDescription}
              onChange={(e) => setFindingDescription(e.target.value)}
              placeholder="Describe the finding or observation..."
              rows={3}
            />
          </div>

          {/* Evidence */}
          <div className="space-y-1.5">
            <Label htmlFor="evidence">Evidence</Label>
            <Textarea
              id="evidence"
              data-testid="input-evidence"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder="Evidence notes, links, or references..."
              rows={2}
            />
          </div>

          {/* Remediation Required */}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="font-medium">Remediation Required</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Track remediation action for this item
              </p>
            </div>
            <Switch
              data-testid="switch-remediation-required"
              checked={remediationRequired}
              onCheckedChange={setRemediationRequired}
            />
          </div>

          {/* Remediation fields — shown when required */}
          {remediationRequired && (
            <div className="space-y-3 pl-1">
              <div className="space-y-1.5">
                <Label htmlFor="remediation-action">Remediation Action</Label>
                <Textarea
                  id="remediation-action"
                  data-testid="input-remediation-action"
                  value={remediationAction}
                  onChange={(e) => setRemediationAction(e.target.value)}
                  placeholder="Describe the required remediation..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="remediation-due-date">Due Date</Label>
                  <Input
                    id="remediation-due-date"
                    data-testid="input-remediation-due-date"
                    type="date"
                    value={remediationDueDate}
                    onChange={(e) => setRemediationDueDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="remediation-completed-date">Completed Date</Label>
                  <Input
                    id="remediation-completed-date"
                    data-testid="input-remediation-completed-date"
                    type="date"
                    value={remediationCompletedDate}
                    onChange={(e) => setRemediationCompletedDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={patchMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save-audit-item"
            onClick={() => patchMutation.mutate()}
            disabled={patchMutation.isPending}
          >
            {patchMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {patchMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
